import { isFirefox } from "./browser"
import { isAbortError } from "./error"

export type FileFormat = "RAW" | "JPEG"

export function getFileId(file: File, path?: string) {
	const prefix = path ? `${path.replace(/\/+$/, "")}/` : ""
	return `${prefix}${file.name}|${file.size}`
}

export function getFileInfo(id: string) {
	const separatorIndex = id.lastIndexOf("|")
	const nameWithPath = separatorIndex >= 0 ? id.slice(0, separatorIndex) : id
	const size = separatorIndex >= 0 ? id.slice(separatorIndex + 1) : ""
	const name = nameWithPath.split("/").at(-1) ?? nameWithPath
	return { name, size: Number(size) }
}

export function getDisambiguatedFileIds(files: File[], filePaths: string[]) {
	const ids = new Array<string>(files.length)
	const firstIndexByBaseId = new Map<string, number>()
	const duplicatedBaseIds = new Set<string>()

	files.forEach((file, index) => {
		const baseId = getFileId(file)
		const firstIndex = firstIndexByBaseId.get(baseId)

		if (firstIndex === undefined) {
			firstIndexByBaseId.set(baseId, index)
			ids[index] = baseId
			return
		}

		if (!duplicatedBaseIds.has(baseId)) {
			const firstFile = files[firstIndex]
			ids[firstIndex] = firstFile ? getFileId(firstFile, filePaths[firstIndex]) : baseId
			duplicatedBaseIds.add(baseId)
		}

		ids[index] = getFileId(file, filePaths[index])
	})

	return ids
}

export function getFilename(source: File | string) {
	if (source instanceof File) {
		return source.name.split(".").at(0) ?? "unknown"
	} else {
		return source.split(".").at(0) ?? "unknown"
	}
}

export function getFileExtension(source: string | File) {
	if (source instanceof File) {
		return source.name.split(".").at(-1)?.toLowerCase() ?? "unknown"
	} else {
		return source.split(".").at(-1)?.toLowerCase() ?? "unknown"
	}
}

export function getFileFormat(source: string | File): FileFormat {
	const ext = getFileExtension(source)
	const fileFormat = ext.toUpperCase()

	if (fileFormat === "JPG" || fileFormat === "JPEG") return "JPEG"
	else return "RAW"
}

export function isJpegFile(source: string | File | undefined) {
	if (!source) {
		return false
	} else {
		const fileFormat = getFileFormat(source)
		return fileFormat === "JPEG"
	}
}

export function isHEICFile(file: File) {
	return file.type === "image/heic" || getFileExtension(file.name) === "heic"
}

export function isAcceptedFile(source: string | File | undefined, accepted: string[]) {
	if (!source) {
		return false
	} else {
		const ext = getFileExtension(source).toLowerCase()
		return accepted.includes(ext) || accepted.includes(`.${ext}`)
	}
}

export async function chooseFile(options: { accept?: string[] }): Promise<File | null> {
	let input = document.querySelector("#fileinput") as HTMLInputElement

	if (!input) {
		input = document.createElement("input")
		input.id = "fileinput"
		input.type = "file"
		input.accept = options.accept?.join(",") ?? ".*"
		input.style.display = "none"
		document.documentElement.appendChild(input)
	}

	return new Promise((resolve) => {
		input.onchange = () => {
			resolve(input.files?.[0] ?? null)
			input.value = ""
		}
		input.click()
	})
}

export async function openFilesWithPicker(pickerOptions: OpenFilePickerOptions): Promise<File[]> {
	const acceptList =
		pickerOptions.types?.flatMap((type) => {
			const entries = type.accept ? Object.entries(type.accept) : []
			return entries.flatMap(([mimeType, extensions]) => {
				const extArray = Array.isArray(extensions) ? extensions : [extensions]
				return [mimeType, ...extArray]
			})
		}) ?? []

	const fallbackToInput = () =>
		new Promise<File[]>((resolve) => {
			const input = document.createElement("input")
			input.type = "file"
			input.multiple = pickerOptions.multiple ?? false
			if (acceptList.length > 0) {
				input.accept = acceptList.join(",")
			}
			input.style.position = "fixed"
			input.style.left = "-9999px"
			document.body.appendChild(input)

			let settled = false
			const handleResolve = (files: File[]) => {
				if (settled) return
				settled = true
				window.removeEventListener("focus", handleFocus)
				input.remove()
				resolve(files)
			}

			const handleFocus = () => {
				// Firefox can emit focus before change; delay cancel resolution to avoid swallowing selected files.
				setTimeout(
					() => {
						const files = input.files ? Array.from(input.files) : []
						handleResolve(files)
					},
					isFirefox ? 300 : 0,
				)
			}

			input.addEventListener("change", () => {
				const files = input.files ? Array.from(input.files) : []
				handleResolve(files)
			})

			window.addEventListener("focus", handleFocus, { once: true })
			input.click()
		})

	if (typeof window !== "undefined" && typeof window.showOpenFilePicker === "function") {
		try {
			const handles = await window.showOpenFilePicker(pickerOptions)
			return Promise.all(handles.map((handle) => handle.getFile()))
		} catch (error) {
			if (isAbortError(error)) return []
			// Safari may expose the API but throw; fall back to the classic input picker.
			console.warn("showOpenFilePicker failed, falling back to input element", error)
			return fallbackToInput()
		}
	}

	return fallbackToInput()
}

export async function getHandleFromPath({
	create,
	fullPath,
	root,
}: {
	create: boolean
	fullPath: string
	root: FileSystemDirectoryHandle
}) {
	let parent: FileSystemDirectoryHandle = root
	const segments = fullPath.split("/")
	for (const segment of segments.slice(0, -1)) {
		if (!segment) {
			continue
		}
		parent = await parent.getDirectoryHandle(segment, { create })
	}

	const finalSegment = segments[segments.length - 1]
	if (!finalSegment) {
		return parent
	}
	return parent.getFileHandle(finalSegment, { create })
}
